import get from "lodash.get";
import { isAlpha, isLAN, isLocal } from "./hosts";

export enum Environment {
  LocalhostWithProd = "^localhost:3001",
  LocalhostWithDev = "(.+)\\.localhost:3001",
  GCP = "gcp", // Default
  GCPDev = "gcpDev",
}

//currently, dataseat.com or devclustername.dataseat.org
const globalDomain = get(window, "CLOUD_CONFIG.GLOBAL_DOMAIN");
const localProdDomain = "dataseat.com"; // Client on localhost, API on prod: hardcoded .com is ok
const localDevTld = process.env.REACT_APP_DEV_TLD ?? "ninja";
const localDevDomain = `{{env}}.dataseat.${localDevTld}`; // Client on localhost, API on devcluster

export const ENVIRONMENTS = {
  // Client on localhost, API on prod
  [Environment.LocalhostWithProd]: {
    account: `dsp-account.${localProdDomain}`,
    api: `dsp-api.${localProdDomain}`,
    publisher: `dsp-publisher-dmp.${localProdDomain}`,
    compression: `compression.${localProdDomain}`,
    reporter: `reporter.${localProdDomain}`,
    billingReporter: {
      url: `billing-reporter.${localProdDomain}`,
      useProxy: false,
      useHttps: true,
    },
    audience: `audience.${localProdDomain}`,
    bidder: {
      url: `bidder.${localProdDomain}`,
      useProxy: false,
      useHttps: true,
    },
    geodb: `geodb.${localProdDomain}`,
    content: { url: "content.inhousedsp.com", useProxy: false, useHttps: true },
    //to test/debug with deployed node-service instead of local, use it url like this:
    //nodeServices: { url: `node-services-alpha.${localProdDomain}`, useProxy: false, useHttps: true },
    nodeServices: { url: "localhost:8081", useProxy: false, useHttps: false },
    predictModelManager: `model-manager.${localProdDomain}`,
    insights: `https://insights.${localProdDomain}`,
    fetchAppInfo: true,
    useAuthHeader: true,
    useSentry: false,
    useGa: false,
    fetchCreativeApprovals: false,
  },
  // Client on localhost, API on devcluster
  [Environment.LocalhostWithDev]: {
    account: `dsp-account.${localDevDomain}`,
    api: `dsp-api.${localDevDomain}`,
    publisher: `dsp-publisher-dmp.${localDevDomain}`,
    compression: `compression.${localDevDomain}`,
    reporter: `reporter.${localDevDomain}`,
    billingReporter: {
      url: `billing-reporter.${localDevDomain}`,
      useProxy: false,
      useHttps: true,
    },
    audience: `audience.${localDevDomain}`,
    bidder: {
      url: `bidder.${localDevDomain}`,
      useProxy: false,
      useHttps: true,
    },
    geodb: `geodb.${localDevDomain}`,
    content: {
      url: "storage.googleapis.com/dataseat-{{env}}-creatives",
      useProxy: false,
      useHttps: true,
    },
    //to test/debug with deployed node-service instead of local, use it url like this:
    //nodeServices: { url: `node-services-alpha.${localDevDomain}`, useProxy: true, useHttps: true },
    nodeServices: { url: "localhost:8081", useProxy: false, useHttps: false },
    predictModelManager: `model-manager.${localDevDomain}`, //not supposed to work on devcluster
    insights: "https://insights.dataseat.com",
    fetchAppInfo: true,
    useAuthHeader: true,
    useSentry: false,
    useGa: false,
    fetchCreativeApprovals: false,
  },
  // GCP, client deployed on alpha/prod
  [Environment.GCP]: {
    account: "http://dsp-account.dsp:8080",
    api: "http://dsp-missioncontrol.dsp:8080",
    publisher: "http://dsp-publisher-dmp.dsp:8080",
    compression: "http://dsp-creative-compression.dsp:8080",
    reporter: "http://dsp-reporter.dsp:8080",
    billingReporter: {
      url: `billing-reporter.${globalDomain}`,
      useProxy: false,
      useHttps: true,
    },
    audience: "http://dsp-audience.dsp:8080",
    bidder: {
      url: `bidder.${globalDomain}`,
      useProxy: false,
      useHttps: true,
    },
    geodb: "http://dsp-geodb.dsp:8080",
    content: "https://content.inhousedsp.com",
    nodeServices: isAlpha
      ? "http://dsp-ui-node-services-sandbox.dsp-ui.svc:8080"
      : "http://dsp-ui-node-services.dsp-ui.svc:8080",
    predictModelManager: `https://model-manager.${globalDomain}`,
    insights: `https://insights.${globalDomain}`,
    fetchAppInfo: true,
    useAuthHeader: true,
    useSentry: true,
    useGa: true,
    fetchCreativeApprovals: true,
  },
  // GCP, client deployed on devcluster
  [Environment.GCPDev]: {
    account: "http://dsp-account.dsp:8080",
    api: "http://dsp-missioncontrol.dsp:8080",
    publisher: "http://dsp-publisher-dmp.dsp:8080",
    compression: "http://dsp-creative-compression.dsp:8080",
    reporter: "http://dsp-reporter.dsp:8080",
    billingReporter: {
      url: `billing-reporter.${globalDomain}`,
      useProxy: false,
      useHttps: true,
    },
    audience: "http://dsp-audience.dsp:8080",
    bidder: {
      url: `bidder.${globalDomain}`,
      useProxy: false,
      useHttps: true,
    },
    geodb: "http://dsp-geodb.dsp:8080",
    content: {
      url: "storage.googleapis.com/dataseat-{{env}}-creatives/",
      useProxy: false,
      useHttps: true,
    },
    nodeServices: isAlpha
      ? "http://dsp-ui-node-services-sandbox.dsp-ui.svc:8080"
      : "http://dsp-ui-node-services.dsp-ui.svc:8080",
    predictModelManager: `model-manager.${globalDomain}`, //not supposed to work on devcluster
    insights: "https://insights.dataseat.com",
    fetchAppInfo: true,
    useAuthHeader: true,
    useSentry: false,
    useGa: false,
    fetchCreativeApprovals: false,
  },
};

export const getEnvironmentKeyAndHost = (force?: Environment) => {
  let host = window.location.host;
  const hostArr = window.location.host.split(".");
  hostArr.shift();

  if (hostArr.length > 1) {
    host = hostArr.join(".");
  }

  let environment =
    force ?? Object.keys(ENVIRONMENTS).find((key) => host.match(key));
  if (!environment) {
    const DEPLOYMENT_ENV = get(window, "CLOUD_CONFIG.DEPLOYMENT_ENV") || "prod";
    environment =
      DEPLOYMENT_ENV === "prod" ? Environment.GCP : Environment.GCPDev;
  }
  if (isLAN) {
    environment = Environment.LocalhostWithProd;
  }
  return { environment, host };
};

export const getEnvironment = (force?: Environment) => {
  const { environment } = getEnvironmentKeyAndHost(force);
  if (environment === Environment.LocalhostWithProd) {
    return ENVIRONMENTS[Environment.LocalhostWithProd];
  } else if (
    [Environment.LocalhostWithDev, Environment.GCPDev].includes(
      environment as Environment
    )
  ) {
    return Object.keys(ENVIRONMENTS[environment as Environment]).reduce(
      (env: any, key: any) => {
        const v = ENVIRONMENTS[environment as Environment][key];

        const devClusterName =
          isLocal || isLAN
            ? (window.location.host.split(".")[0] ?? "") //osokorki.localhost:3001
            : (window.location.host.split(".")[1] ?? ""); //admin-alpha.osokorki.dataseat.org or admin.angel.dataseat.org

        if (typeof v === "boolean") {
          return {
            ...env,
            [key]: v,
          };
        } else if (typeof v === "string") {
          return {
            ...env,
            [key]: v.replaceAll("{{env}}", devClusterName),
          };
        } else {
          return {
            ...env,
            [key]: {
              ...ENVIRONMENTS[environment as Environment][key],
              url: v.url.replaceAll("{{env}}", devClusterName),
            },
          };
        }
      },
      {}
    );
  } else {
    return {
      ...ENVIRONMENTS[Environment.GCP],
      billingReporter: {
        ...ENVIRONMENTS[Environment.GCP].billingReporter,
        url: `${ENVIRONMENTS[Environment.GCP].billingReporter.url}`,
      },
      nodeServices: `${ENVIRONMENTS[Environment.GCP].nodeServices}`,
      insights: `${ENVIRONMENTS[Environment.GCP].insights}`,
    };
  }
};

export const checkForMisconfigurations = (endpoint: string, environment) => {
  if (
    typeof environment[endpoint] !==
      typeof getEnvironment(Environment.GCP)[endpoint] &&
    window.location.host.includes("localhost")
  ) {
    //eslint-disable-next-line  no-console
    console.log(
      `%cMisconfiguration found within '${endpoint}' endpoint. Please ensure 'GCP' environment has the same structure as 'Localhost' to prevent errors in dev/prod environments.`,
      "color: white; font-size: 28px; background-color: red; padding: 20px; text-align: center;"
    );
  }
};

export const getEndpoint = (endpoint: string): string => {
  const environment = getEnvironment();

  // checkForMisconfigurations(endpoint, environment);

  if (environment[endpoint]) {
    if (typeof environment[endpoint] === "string") {
      return `${window.location.origin}/proxy/${environment[endpoint]}`;
    } else if (environment[endpoint]?.useProxy) {
      return `${window.location.origin}/proxy/${environment[endpoint].url}`;
    } else {
      const e = environment[endpoint];

      return `${e.useHttps ? "https" : "http"}://${
        e.useLocationHost ? `${window.location.host}/proxy/` : ""
      }${e.prefix ? `${e.prefix}.` : ""}${e.url}${e.suffix || ""}`;
    }
  } else {
    //eslint-disable-next-line  no-console
    console.error(`${endpoint} is not defined`);
    return "";
  }
};

export const proxifyUrl = (url: string) => {
  if (url.includes("/proxy/")) return url;
  return `${window.location.origin}/proxy/${url}`;
};
export const deproxifyUrl = (url: string) => {
  if (!url.includes("/proxy/")) return url;
  //use part after /proxy/
  const newUrl = url.split("/proxy/")[1];
  if (newUrl.startsWith("http") || newUrl.startsWith("https")) {
    return newUrl;
  }
  return `https://${newUrl}`;
};
